import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { industryApi } from 'api/industryApi';

export const fetchIndustries = createAsyncThunk(
  'industries/fetchIndustries',
  async (options = {}, { rejectWithValue }) => {
    try {
      return await industryApi.fetchAllIndustries(options);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createIndustry = createAsyncThunk(
  'industries/createIndustry',
  async (industryData, { rejectWithValue }) => {
    try {
      return await industryApi.createIndustry(industryData);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateIndustry = createAsyncThunk(
  'industries/updateIndustry',
  async ({ id, industryData }, { rejectWithValue }) => {
    try {
      return await industryApi.updateIndustry(id, industryData);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteIndustry = createAsyncThunk(
  'industries/deleteIndustry',
  async (id, { rejectWithValue }) => {
    try {
      await industryApi.deleteIndustry(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchIndustryById = createAsyncThunk(
  'industries/fetchIndustryById',
  async (id, { rejectWithValue }) => {
    try {
      return await industryApi.fetchIndustryById(id);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const industrySlice = createSlice({
  name: 'industries',
  initialState: {
    industries: [],
    currentIndustry: null,
    loading: false,
    submitting: false,
    error: null,
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
      pages: 0,
    },
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentIndustry: (state, action) => {
      state.currentIndustry = action.payload;
    },
    clearCurrentIndustry: (state) => {
      state.currentIndustry = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Industries
      .addCase(fetchIndustries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIndustries.fulfilled, (state, action) => {
        state.loading = false;
        state.industries = action.payload.industries || [];
        state.pagination = action.payload.pagination || state.pagination;
      })
      .addCase(fetchIndustries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Industry
      .addCase(createIndustry.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(createIndustry.fulfilled, (state, action) => {
        state.submitting = false;
        state.industries.push(action.payload);
      })
      .addCase(createIndustry.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      })
      // Update Industry
      .addCase(updateIndustry.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(updateIndustry.fulfilled, (state, action) => {
        state.submitting = false;
        const index = state.industries.findIndex(
          industry => industry.id === action.payload.id
        );
        if (index !== -1) {
          state.industries[index] = action.payload;
        }
        if (state.currentIndustry?.id === action.payload.id) {
          state.currentIndustry = action.payload;
        }
      })
      .addCase(updateIndustry.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      })
      // Delete Industry
      .addCase(deleteIndustry.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(deleteIndustry.fulfilled, (state, action) => {
        state.submitting = false;
        state.industries = state.industries.filter(
          industry => industry.id !== action.payload
        );
      })
      .addCase(deleteIndustry.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      })
      // Fetch Industry by ID
      .addCase(fetchIndustryById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIndustryById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentIndustry = action.payload;
      })
      .addCase(fetchIndustryById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setCurrentIndustry, clearCurrentIndustry } = industrySlice.actions;

// Selectors
export const selectIndustries = (state) => state.industries.industries;
export const selectCurrentIndustry = (state) => state.industries.currentIndustry;
export const selectLoading = (state) => state.industries.loading;
export const selectSubmitting = (state) => state.industries.submitting;
export const selectError = (state) => state.industries.error;
export const selectPagination = (state) => state.industries.pagination;

export default industrySlice.reducer;
